import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line, CartesianGrid,
} from "recharts";
import Select from "react-select";
import privateApi from "../../apis/privateApi";
import "./Dashboard.css";

const getPanelPosition = (ref) => {
    const rect = ref.current?.getBoundingClientRect();

    if (!rect) {
        return { top: 0, left: 0 };
    }

    return {
        position: "absolute",
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
        minWidth: rect.width,
        zIndex: 9999,
    };
};
const GeoPanelPortal = ({ children }) => {
    return ReactDOM.createPortal(children, document.body);
};

/* ─── Theme ─────────────────────────────────────────────────── */
const C = {
    forest: "#012D1A",
    emerald: "#006B3C",
    emeraldLight: "#00925A",
    mint: "#00C06A",
    gold: "#C9962A",
    amber: "#E6A700",
    danger: "#D9302F",
    muted: "#6B7F70",
    border: "#D5DDD7",
    text: "#0E1F14",
    bg: "#F0F4F1",
};

const CHART_COLORS = [C.emerald, C.gold, C.mint, C.amber, C.forest];

/* ─── Helpers ───────────────────────────────────────────────── */
// const fmt = (v) => {
//     if (v >= 1e9) return "₦" + (v / 1e9).toFixed(1) + "B";
//     if (v >= 1e6) return "₦" + (v / 1e6).toFixed(1) + "M";
//     if (v >= 1e3) return "₦" + (v / 1e3).toFixed(1) + "K";
//     return "₦" + String(v);
// };

const fmtAxis = (v) => {
    if (v >= 1e9) return (v / 1e9).toFixed(1) + "B";
    if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
    if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
    return String(v);
};

const flattenSegments = (segments, level = 0) => {
    let result = [];
    segments.forEach(item => {
        result.push({
            value: item._id,
            label: "\u00A0".repeat(level * 3) + item.name,
            level, isLeaf: !item.hasChildren,
        });
        if (item.children?.length)
            result = result.concat(flattenSegments(item.children, level + 1));
    });
    return result;
};

const statusClass = (status) => {
    if (status === "Overspent") return "danger";
    if (status === "Risk") return "risk";
    return "safe";
};

/* ─── Custom Tooltip ─────────────────────────────────────────── */
const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: C.forest, color: "#fff",
            padding: "10px 16px", borderRadius: 10,
            fontSize: 13, boxShadow: "0 8px 24px rgba(1,45,26,.25)",
            minWidth: 140,
        }}>
            {label && (
                <p style={{ fontSize: 11, fontWeight: 700, opacity: .6, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                    {label}
                </p>
            )}
            {payload.map((p, i) => (
                <p key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, margin: "2px 0" }}>
                    <span style={{ opacity: .75 }}>{p.name}</span>
                    <strong>{typeof p.value === "number" ? "₦" + p.value.toLocaleString() : p.value}</strong>
                </p>
            ))}
        </div>
    );
};

/* ─── react-select styles ────────────────────────────────────── */
const selectStyles = {
    control: (b, s) => ({
        ...b,
        borderColor: s.isFocused ? C.emerald : C.border,
        boxShadow: s.isFocused ? `0 0 0 3px rgba(0,107,60,.18)` : "none",
        "&:hover": { borderColor: C.emerald },
        borderRadius: 6, fontSize: 13, minHeight: 36,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }),
    option: (b, s) => ({
        ...b, fontSize: 13,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: s.isSelected ? C.emerald : s.isFocused ? "rgba(0,107,60,.07)" : "#fff",
        color: s.isSelected ? "#fff" : C.text,
    }),
    singleValue: (b) => ({ ...b, color: C.text }),
    menu: (b) => ({ ...b, zIndex: 1000, borderRadius: 10, border: `1px solid ${C.border}`, boxShadow: "0 12px 40px rgba(1,45,26,.14)" }),
    placeholder: (b) => ({ ...b, color: C.muted, fontSize: 13 }),
    clearIndicator: (b) => ({ ...b, color: C.muted, "&:hover": { color: C.danger } }),
    dropdownIndicator: (b) => ({ ...b, color: C.muted }),
    indicatorSeparator: (b) => ({ ...b, background: C.border }),
};

/* ─── Geo Dropdown ───────────────────────────────────────────── */
const GeoDropdown = ({ label, items, selected, onToggle, search, onSearch, searchPlaceholder, error, renderChildren }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const panelRef = useRef(null);
    const count = selected.length;

    useEffect(() => {
        const handleMouseDown = (e) => {
            if (
                ref.current?.contains(e.target) ||
                panelRef.current?.contains(e.target)
            ) {
                return;
            }
            setOpen(false);
        };

        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, []);

    return (
        <div className="db-geo-dropdown" ref={ref}>
            <button
                className={`db-geo-btn ${open ? "active" : ""} ${count ? "has-selection" : ""}`}
                onClick={() => setOpen(p => !p)}
            >
                {label}
                {count > 0 && <span className="db-geo-btn-count">{count}</span>}
                <span className="db-geo-btn-chevron">▼</span>
            </button>

            {/* {open && (
                <div className="db-geo-panel">
                    <div className="db-geo-panel-search">
                        <input
                            value={search}
                            onChange={e => onSearch(e.target.value)}
                            placeholder={searchPlaceholder}
                            autoFocus
                        />
                    </div>
                    {error && <p className="db-geo-error">⚠ Failed to load {label}</p>}
                    <div className="db-geo-panel-list">
                        {renderChildren
                            ? renderChildren()
                            : items.map(item => (
                                <label key={item._id} className="db-geo-item">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(item._id)}
                                        onChange={() => onToggle(item._id)}
                                    />
                                    {item.name === "Enugu" ? "LGY – Enugu" : item.name}
                                </label>
                            ))
                        }
                    </div>
                </div>
            )} */}
            {open && (
                <GeoPanelPortal>
                    <div
                        ref={panelRef}
                        className="db-geo-panel"
                        style={getPanelPosition(ref)}
                    >
                        <div className="db-geo-panel-search">
                            <input
                                value={search}
                                onChange={e => onSearch(e.target.value)}
                                placeholder={searchPlaceholder}
                                autoFocus
                            />
                        </div>

                        {error && <p className="db-geo-error">⚠ Failed to load {label}</p>}

                        <div className="db-geo-panel-list">
                            {renderChildren
                                ? renderChildren()
                                : items.map(item => (
                                    <label key={item._id} className="db-geo-item">
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(item._id)}
                                            onChange={() => onToggle(item._id)}
                                        />
                                        {item.name?.toLowerCase() === "enugu" ? "LGY" : item.name}
                                    </label>
                                ))
                            }
                        </div>
                    </div>
                </GeoPanelPortal>
            )}
        </div>
    );
};

/* ─── KPI Card ───────────────────────────────────────────────── */
const KPI_ICONS = ["₦", "📈", "🏦", "✅", "⚠"];

const KpiCard = ({ kpi, index }) => (
    <div className="db-kpi-card" style={{ animationDelay: `${index * 70}ms` }}>
        <span className="db-kpi-card-bg-icon">{KPI_ICONS[index % KPI_ICONS.length]}</span>
        <p className="db-kpi-label">{kpi.title}</p>
        <p className="db-kpi-value">{kpi.value}</p>
        <p className="db-kpi-sub">Fiscal Year {new Date().getFullYear() - 1}</p>
    </div>
);

/* ─── Segment Select wrapper ─────────────────────────────────── */
const SegSelect = ({ label, options, value, onChange, placeholder }) => (
    <div className="db-seg-group">
        <span className="db-seg-label">{label}</span>
        <Select
            classNamePrefix="react-select"
            styles={selectStyles}
            options={options}
            value={options.find(o => o.value === value) || null}
            onChange={opt => onChange(opt?.value || "")}
            placeholder={placeholder}
            isSearchable isClearable
        />
    </div>
);

/* ════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════ */
const Dashboard = () => {

    /* ── Data ── */
    const [kpiData, setKpiData] = useState([]);
    const [budgetData, setBudgetData] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [burnRateData, setBurnRateData] = useState([]);
    const [riskData, setRiskData] = useState([]);

    /* ── Filters ── */
    const [location, setLocation] = useState("");
    const [selectedStates, setSelectedStates] = useState([]);
    const [selectedLgas, setSelectedLgas] = useState([]);
    const [selectedWards, setSelectedWards] = useState([]);
    const [selectedBudgetTypes, setSelectedBudgetTypes] = useState([]);

    const [administrativeUnitId, setAdministrativeUnitId] = useState("");
    const [programmeId, setProgrammeId] = useState("");
    const [economicSegmentId, setEconomicSegmentId] = useState("");
    const [fundId, setFundId] = useState("");
    const [functionalSegmentId, setFunctionalSegmentId] = useState("");

    /* ── Location lists ── */
    const [states, setStates] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [wards, setWards] = useState([]);
    const [statesError, setStatesError] = useState(false);
    const [lgasError, setLgasError] = useState(false);
    const [wardsError, setWardsError] = useState(false);

    /* ── Segment lists ── */
    const [administrativeUnits, setAdministrativeUnits] = useState([]);
    const [programmes, setProgrammes] = useState([]);
    const [economicSegments, setEconomicSegments] = useState([]);
    const [funds, setFunds] = useState([]);
    const [functions, setFunctions] = useState([]);

    /* ── Search ── */
    const [stateSearch, setStateSearch] = useState("");
    const [lgaSearch, setLgaSearch] = useState("");
    const [wardSearch, setWardSearch] = useState("");

    /* ── UI ── */
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    /* ── Derived ── */
    const filteredStates = states.filter(s =>
        s.name.toLowerCase().includes(stateSearch.toLowerCase())
    );
    const filteredLgas = lgas.filter(l =>
        l.name.toLowerCase().includes(lgaSearch.toLowerCase())
    );
    const filteredWards = (wards || [])
        .map(g => ({ ...g, wards: (g.wards || []).filter(w => (w.name ?? "").toLowerCase().includes((wardSearch ?? "").toLowerCase())) }))
        .filter(g => g.wards.length > 0);

    const administrativeUnitOptions = flattenSegments(administrativeUnits);
    const programmeOptions = flattenSegments(programmes);
    const economicSegmentOptions = flattenSegments(economicSegments);
    const fundOptions = flattenSegments(funds);
    const functionOptions = flattenSegments(functions);

    const hasFilters = !!(
        selectedStates.length || selectedLgas.length || selectedWards.length ||
        selectedBudgetTypes.length || administrativeUnitId || programmeId ||
        economicSegmentId || fundId || functionalSegmentId || location
    );

    /* ── Toggle helper ── */
    const toggle = useCallback((setter) => (id) =>
        setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]),
        []);

    /* ── Fetch location lists ── */
    useEffect(() => {
        (async () => {
            const [statesRes, lgasRes, wardsRes] = await Promise.allSettled([
                privateApi.get("/api/state/budgets/locations"),
                privateApi.get("/api/lga/budgets/locations"),
                privateApi.get("/api/ward/budgets/locations"),
            ]);
            if (statesRes.status === "fulfilled") setStates(statesRes.value.data.data || []); else setStatesError(true);
            if (lgasRes.status === "fulfilled") setLgas(lgasRes.value.data.data || []); else setLgasError(true);
            if (wardsRes.status === "fulfilled") setWards(wardsRes.value.data.data || []); else setWardsError(true);
        })();
    }, []);

    /* ── Fetch segments when level changes ── */
    useEffect(() => {
        if (!location) return;
        (async () => {
            const [adminRes, progRes, econRes, fundRes, funcRes] = await Promise.allSettled([
                privateApi.get(`/api/get-create-budget-line-administrative-units?level=${location}`),
                privateApi.get(`/api/get-create-budget-line-programmes?level=${location}`),
                privateApi.get(`/api/get-create-budget-line-economic-segments?level=${location}`),
                privateApi.get(`/api/get-create-budget-line-funds?level=${location}`),
                privateApi.get(`/api/get-create-budget-line-functional-segments?level=${location}`),
            ]);
            if (adminRes.status === "fulfilled") setAdministrativeUnits(adminRes.value.data.data || []);
            if (progRes.status === "fulfilled") setProgrammes(progRes.value.data.data || []);
            if (econRes.status === "fulfilled") setEconomicSegments(econRes.value.data.data || []);
            if (fundRes.status === "fulfilled") setFunds(fundRes.value.data.data || []);
            if (funcRes.status === "fulfilled") setFunctions(funcRes.value.data.data || []);
        })();
    }, [location]);

    /* ── Fetch dashboard data ── */
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                const p = new URLSearchParams();

                if (location) p.append("location", location);

                if (selectedStates.length) {
                    p.append("stateIds", selectedStates.join(","));
                }

                if (selectedLgas.length) {
                    p.append("lgaIds", selectedLgas.join(","));
                }

                if (selectedWards.length) {
                    p.append("wardIds", selectedWards.join(","));
                }

                if (administrativeUnitId) {
                    p.append("administrativeUnitIds", administrativeUnitId);
                }

                if (programmeId) {
                    p.append("programmeIds", programmeId);
                }

                if (economicSegmentId) {
                    p.append("economicSegmentIds", economicSegmentId);
                }

                if (fundId) {
                    p.append("fundIds", fundId);
                }

                if (functionalSegmentId) {
                    p.append("functionalSegmentIds", functionalSegmentId);
                }

                if (selectedBudgetTypes.length) {
                    p.append("budgetTypes", selectedBudgetTypes.join(","));
                }

                const { data } = await privateApi.get(
                    `/api/dashboard?${p.toString()}`
                );

                const res = data.data;

                setKpiData(res.kpis || []);
                setBudgetData(res.budgetVsActual || []);
                setPieData(res.fundDistribution || []);
                setBurnRateData(res.burnRate || []);
                setRiskData(res.riskMonitor || []);

            } catch {
                setError("Failed to load dashboard data. Please try again.");
            } finally {
                setLoading(false);
            }
        })();
    }, [
        location,
        selectedStates,
        selectedLgas,
        selectedWards,
        selectedBudgetTypes,
        administrativeUnitId,
        programmeId,
        economicSegmentId,
        fundId,
        functionalSegmentId
    ]);

    const resetFilters = () => {
        setSelectedStates([]); setSelectedLgas([]); setSelectedWards([]);
        setSelectedBudgetTypes([]);
        setStateSearch(""); setLgaSearch(""); setWardSearch("");
        setAdministrativeUnitId(""); setProgrammeId(""); setEconomicSegmentId("");
        setFundId(""); setFunctionalSegmentId(""); setLocation("");
    };

    /* ═══ RENDER ══════════════════════════════════════════════════ */
    return (
        <div className="db-page">

            {/* ── HEADER ─────────────────────────────────────────── */}
            <header className="db-header">
                <div className="db-header-left">
                    <div className="db-header-eyebrow">
                        <span className="db-flag-badge">
                            <span className="db-flag-badge-dot" />
                            🇳🇬 &nbsp;Enugu State, Federal Republic of Nigeria
                        </span>
                    </div>
                    <h1 className="db-title">Budget Management Dashboard</h1>
                    <p className="db-subtitle">Public Finance Oversight &amp; Performance Monitoring Portal</p>
                </div>
                <div className="db-header-right">
                    <div className="db-header-stat">
                        <div className="db-header-stat-value">  FY {new Date().getFullYear() - 1}</div>
                        <div className="db-header-stat-label">Fiscal Year</div>
                    </div>
                    <div className="db-header-stat">
                        <div className="db-header-stat-value">NGN</div>
                        <div className="db-header-stat-label">Currency</div>
                    </div>
                </div>
            </header>

            {/* ── FILTER TOOLBAR ─────────────────────────────────── */}
            <div className="db-filter-toolbar">
                <span className="db-filter-label">Filters</span>

                <GeoDropdown
                    label="LGY"
                    items={filteredStates}
                    selected={selectedStates}
                    onToggle={toggle(setSelectedStates)}
                    search={stateSearch}
                    onSearch={setStateSearch}
                    searchPlaceholder="Search LGY..."
                    error={statesError}
                />

                <GeoDropdown
                    label="LGA"
                    items={filteredLgas}
                    selected={selectedLgas}
                    onToggle={toggle(setSelectedLgas)}
                    search={lgaSearch}
                    onSearch={setLgaSearch}
                    searchPlaceholder="Search LGA..."
                    error={lgasError}
                />

                <GeoDropdown
                    label="Ward"
                    items={[]}
                    selected={selectedWards}
                    onToggle={toggle(setSelectedWards)}
                    search={wardSearch}
                    onSearch={setWardSearch}
                    searchPlaceholder="Search ward..."
                    error={wardsError}
                    renderChildren={() =>
                        filteredWards.map(group => (
                            <div key={group.lgaId}>
                                <div className="db-geo-group-title">{group.lgaName}</div>
                                {group.wards.map(ward => (
                                    <label key={ward._id} className="db-geo-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedWards.includes(ward._id)}
                                            onChange={() => toggle(setSelectedWards)(ward._id)}
                                        />
                                        {ward.name}
                                    </label>
                                ))}
                            </div>
                        ))
                    }
                />

                <div className="db-filter-divider" />

                <button
                    className={`db-pill-btn ${selectedBudgetTypes.includes("REVENUE") ? "checked" : ""}`}
                    onClick={() => toggle(setSelectedBudgetTypes)("REVENUE")}
                >
                    {selectedBudgetTypes.includes("REVENUE") && <span className="db-pill-check">✓</span>}
                    Revenue
                </button>

                <button
                    className={`db-pill-btn ${selectedBudgetTypes.includes("EXPENDITURE") ? "checked" : ""}`}
                    onClick={() => toggle(setSelectedBudgetTypes)("EXPENDITURE")}
                >
                    {selectedBudgetTypes.includes("EXPENDITURE") && <span className="db-pill-check">✓</span>}
                    Expenditure
                </button>

                <div className="db-filter-spacer" />

                {hasFilters && (
                    <button className="db-reset-btn" onClick={resetFilters}>
                        ✕&nbsp; Reset Filters
                    </button>
                )}
            </div>

            {/* ── SEGMENT BAR ────────────────────────────────────── */}
            <div className="db-segment-bar">
                <div className="db-seg-group">
                    <span className="db-seg-label">Budget Level</span>
                    <Select
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        options={[{ value: "state", label: "State" }, { value: "lga", label: "LGA" }]}
                        value={location ? { value: location, label: location.charAt(0).toUpperCase() + location.slice(1) } : null}
                        onChange={opt => setLocation(opt?.value || "")}
                        placeholder="Select level..."
                        isClearable
                    />
                </div>

                <SegSelect label="Admin Unit" options={administrativeUnitOptions} value={administrativeUnitId} onChange={setAdministrativeUnitId} placeholder="Select unit..." />
                <SegSelect label="Economic Seg." options={economicSegmentOptions} value={economicSegmentId} onChange={setEconomicSegmentId} placeholder="Select segment..." />
                <SegSelect label="Functional Seg." options={functionOptions} value={functionalSegmentId} onChange={setFunctionalSegmentId} placeholder="Select function..." />
                <SegSelect label="Fund Seg." options={fundOptions} value={fundId} onChange={setFundId} placeholder="Select fund..." />
                <SegSelect label="Programme Seg." options={programmeOptions} value={programmeId} onChange={setProgrammeId} placeholder="Select programme..." />
            </div>

            {/* ── CONTENT ────────────────────────────────────────── */}
            {loading ? (
                <div className="db-loading-wrap">
                    <div className="db-loading-ring" />
                    <p className="db-loading-text">Loading dashboard data...</p>
                </div>
            ) : error ? (
                <div className="db-error-box">
                    <span>⚠</span> {error}
                </div>
            ) : (
                <>
                    {/* KPIs */}
                    <div className="db-kpi-grid">
                        {kpiData.map((kpi, i) => <KpiCard key={i} kpi={kpi} index={i} />)}
                    </div>

                    {/* Charts row */}
                    <div className="db-charts-row">
                        {/* Budget vs Actual */}
                        <div className="db-card">
                            <div className="db-card-header">
                                <h3 className="db-card-title">
                                    <span className="db-card-title-dot" />
                                    Budget vs Actual
                                </h3>
                                <div className="db-chart-legend">
                                    <span className="db-chart-legend-item">
                                        <span className="db-chart-legend-dot" style={{ background: C.emerald }} /> Budget
                                    </span>
                                    <span className="db-chart-legend-item">
                                        <span className="db-chart-legend-dot" style={{ background: C.danger }} /> Spent
                                    </span>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={budgetData} barCategoryGap="32%" barGap={4}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#EBF0EC" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: C.muted, fontFamily: "'Plus Jakarta Sans',sans-serif" }} axisLine={false} tickLine={false} />
                                    <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: C.muted, fontFamily: "'Plus Jakarta Sans',sans-serif" }} axisLine={false} tickLine={false} />
                                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,107,60,.04)", radius: 4 }} />
                                    <Bar dataKey="budget" fill={C.emerald} radius={[5, 5, 0, 0]} name="Budget" />
                                    <Bar dataKey="spent" fill={C.danger} radius={[5, 5, 0, 0]} name="Spent" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Fund Distribution */}
                        <div className="db-card">
                            <div className="db-card-header">
                                <h3 className="db-card-title">
                                    <span className="db-card-title-dot" style={{ background: C.gold }} />
                                    Fund Distribution
                                </h3>
                            </div>
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        dataKey="value"
                                        outerRadius={105}
                                        innerRadius={52}
                                        paddingAngle={3}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        labelLine={{ stroke: C.muted, strokeWidth: 1 }}
                                    >
                                        {pieData.map((_, i) => (
                                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    {/* <Tooltip
                                        content={<ChartTooltip />}
                                        formatter={v => "₦" + Number(v).toLocaleString()}
                                    /> */}
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;

                                            const item = payload[0];

                                            const total = pieData.reduce(
                                                (sum, fund) => sum + fund.value,
                                                0
                                            );

                                            const percentage =
                                                total > 0
                                                    ? ((item.value / total) * 100).toFixed(2)
                                                    : "0.00";

                                            return (
                                                <div
                                                    style={{
                                                        background: C.forest,
                                                        color: "#fff",
                                                        padding: "10px 16px",
                                                        borderRadius: 10,
                                                    }}
                                                >
                                                    <p>{item.name}</p>

                                                    <p>
                                                        Value: ₦{Number(item.value).toLocaleString()}
                                                    </p>

                                                    <p>
                                                        Percentage: {percentage}%
                                                    </p>
                                                </div>
                                            );
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Burn Rate */}
                    <div className="db-card-full">
                        <div className="db-card-header">
                            <h3 className="db-card-title">
                                <span className="db-card-title-dot" style={{ background: C.amber }} />
                                Budget Burn Rate
                            </h3>
                        </div>
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={burnRateData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#EBF0EC" vertical={false} />
                                <XAxis dataKey="month" tick={{ fontSize: 12, fill: C.muted, fontFamily: "'Plus Jakarta Sans',sans-serif" }} axisLine={false} tickLine={false} />
                                <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: C.muted, fontFamily: "'Plus Jakarta Sans',sans-serif" }} axisLine={false} tickLine={false} />
                                <Tooltip content={<ChartTooltip />} />
                                <Line
                                    type="monotone"
                                    dataKey="spent"
                                    stroke={C.emerald}
                                    strokeWidth={2.5}
                                    dot={{ r: 4.5, fill: "#fff", stroke: C.emerald, strokeWidth: 2.5 }}
                                    activeDot={{ r: 7, fill: C.emerald, strokeWidth: 0 }}
                                    name="Spent"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Risk Monitor */}
                    <div className="db-card-full">
                        <div className="db-card-header">
                            <h3 className="db-card-title">
                                <span className="db-card-title-dot" style={{ background: C.danger }} />
                                Overspending &amp; Risk Monitor
                            </h3>
                        </div>

                        <div className="db-table-wrap">
                            <table className="db-table">
                                <thead>
                                    <tr>
                                        <th>Budget Line</th>
                                        <th>Allocated (₦)</th>
                                        <th>Spent (₦)</th>
                                        <th style={{ minWidth: 180 }}>Utilisation</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {riskData.map((item, i) => {
                                        const sc = statusClass(item.status);
                                        const pct = Math.min(item.percentage, 100);
                                        return (
                                            <tr key={i}>
                                                <td>{item.name}</td>
                                                <td>{item.allocated.toLocaleString()}</td>
                                                <td>{item.spent.toLocaleString()}</td>
                                                <td>
                                                    <div className="db-progress-cell">
                                                        <div className="db-progress-track">
                                                            <div
                                                                className={`db-progress-fill ${sc}`}
                                                                style={{ width: `${pct}%` }}
                                                            />
                                                        </div>
                                                        <span className={`db-progress-pct ${sc}`}>{item.percentage}%</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`db-status-badge ${sc}`}>
                                                        <span className="db-status-dot" />
                                                        {item.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
